/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React from 'react';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import { formatChatPreviewTime } from '../../../helpers';
import { ChatMessage, useChatChatter } from '../../../redux/services/nebulaChat';
import { focusOnConversationThreadMessage, selectConversationById } from '../../../redux/slices/chatNSlice';
import EntityIcon from '../../icons/EntityIcon';
import { MarkdownContainer } from '../../../pages/chatG/MarkdownComponents';
import HighlightedText, { splitTextToRegex } from '../../StyledComponents/HighlightedText';
import threadStyles from './ThreadRow/ThreadRow.module.scss';

interface MessageSearchRowProps {
    message: ChatMessage;
    searchQuery?: string;
}

const PREVIEW_LENGTH = 150;

const MessageMessageContent: React.FC<{
    message: ChatMessage;
    searchQuery?: string;
}> = ({ message, searchQuery }) => {
    const chatter = useChatChatter({ userUuid: message.from });

    const contentPreview = message.content && message.content.length > PREVIEW_LENGTH
        ? `${message.content.substring(0, PREVIEW_LENGTH)}...`
        : message.content;

    return (
        <>
            <EntityIcon
                type='user'
                details={{
                    sip_name: chatter.sip_name,
                    display_name: chatter.name,
                    avatar_key: chatter.file_key,
                    blf: true,
                    uuid: chatter.uuid,
                }}
            />
            <div className={threadStyles.message_content}>
                <div className={threadStyles.message_content_header} data-is-blank='true'>
                    <h4>{chatter.name}</h4>
                    <p className={threadStyles.time}>{formatChatPreviewTime(message.time)}</p>
                </div>
                <p>
                    {searchQuery
                        ? <HighlightedText exp={splitTextToRegex(searchQuery)} text={contentPreview || ''} />
                        : <MarkdownContainer plain>{contentPreview}</MarkdownContainer>
                    }
                    {!message.content && message.files?.map(f => f.name).join(', ')}
                </p>
            </div>
        </>
    );
};

const MessageSearchRow: React.FC<MessageSearchRowProps> = ({ message, searchQuery }) => {
    const dispatch = useTypedDispatch();
    const conversation = useTypedSelector(state => selectConversationById(state, message.to));
    const parentConversation = useTypedSelector(state =>
        message.to_type === 'thread' && conversation?.parent_message
            ? selectConversationById(state, conversation.parent_message.to)
            : undefined
    );

    const isThread = message.to_type === 'thread';
    const parentMessage = isThread ? conversation?.parent_message : undefined;

    const onRowClick = () => {
        if (isThread && parentMessage) {
            dispatch(focusOnConversationThreadMessage({
                parentConversationUuid: parentMessage.to,
                messageUuid: parentMessage.uuid,
            }));
        } else {
            dispatch(focusOnConversationThreadMessage({
                parentConversationUuid: message.to,
                messageUuid: message.uuid,
            }));
        }
    };

    let locationLabel: React.ReactNode;
    if (message.to_type === 'user') {
        locationLabel = 'To You';
    } else if (isThread) {
        const parentName = parentConversation?.display_name;
        locationLabel = <>In Thread on {parentName ? <span>{parentName}</span> : null}</>;
    } else {
        locationLabel = <>In&nbsp;<span>{conversation?.display_name || ''}</span></>;
    }

    return (
        <div
            className={threadStyles.thread_row_container}
            style={{ minHeight: 'fit-content' }}
            onClick={onRowClick}
            onKeyDown={e => { if (e.key === 'Enter') onRowClick(); }}
            tabIndex={0}
        >
            <div className={threadStyles.row_header}>
                <span className={threadStyles.parent_chat_button}>
                    {locationLabel}
                </span>
            </div>
            <div className={threadStyles.messages_container}>
                <div className={threadStyles.original_message}>
                    <MessageMessageContent message={isThread && parentMessage ? parentMessage : message} />
                </div>
                {isThread && parentMessage && (
                    <div className={threadStyles.thread_comment}>
                        <div className={threadStyles.original_message}>
                            <MessageMessageContent
                                message={message}
                                searchQuery={searchQuery}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageSearchRow;
